/**
 * RotationCadenceSparkline — tiny SVG visual of when a user's
 * public key was last rotated, up to the last N rotations.
 *
 * Fetches `/users/me/key-history` (or `/users/{id}/key-history`),
 * plots each rotation's `created_at` as a dot on a 42-day axis
 * (six 7-day cycles). "Today" sits at the right edge; older
 * rotations drift left; anything older than the window folds in
 * at the leftmost position with a faded colour so the viewer
 * still knows history exists beyond the visible range.
 *
 * Works as both a self-cadence proof (Settings → Privacy) AND a
 * peer-cadence trust signal (SafetyNumberDialog): a peer who
 * rotates on a regular 7-day rhythm is visibly a well-behaved
 * participant; one who hasn't rotated in months is a different
 * kind of signal.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { classifyRotationHealth, HEALTH_DOT_CLASS } from '../hooks/useRotationHealth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_DAYS = 42;     // six default rotation cycles
const WIDTH = 180;
const HEIGHT = 28;
const DOT_R = 3;
const PADDING_X = 6;

const _xForTs = (ts) => {
  const now = Date.now();
  const windowMs = WINDOW_DAYS * 86400_000;
  const ageMs = now - ts;
  if (ageMs >= windowMs) return PADDING_X;         // clamp to far-left
  const frac = 1 - ageMs / windowMs;               // 0 = oldest, 1 = now
  return PADDING_X + frac * (WIDTH - 2 * PADDING_X);
};

const _fmt = (ts) => {
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
};

export const RotationCadenceSparkline = ({
  userId = null,        // omit for self
  className = '',
  label = 'Rotation cadence',
}) => {
  const [entries, setEntries] = useState(null);   // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Never run in duress — exposes the authenticated session.
      if (typeof window !== 'undefined' &&
          sessionStorage.getItem('gs_duress_mode') === '1') {
        setEntries([]);
        return;
      }
      try {
        const url = userId
          ? `${API}/users/${userId}/key-history`
          : `${API}/users/me/key-history`;
        const r = await axios.get(url);
        const rows = (r.data?.entries || [])
          .map((e) => ({ ts: Date.parse(e.created_at), fp: e.fingerprint }))
          .filter((e) => !Number.isNaN(e.ts))
          .sort((a, b) => a.ts - b.ts);
        if (!cancelled) setEntries(rows.slice(-8));
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (error) return null;           // silent — not worth a UI slot
  if (entries === null) {
    return (
      <div
        className={`text-[11px] text-slate-500 ${className}`}
        data-testid="rotation-sparkline-loading"
      >
        Loading cadence…
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div
        className={`text-[11px] text-slate-500 ${className}`}
        data-testid="rotation-sparkline-empty"
      >
        No rotation history yet.
      </div>
    );
  }

  const cutoff = Date.now() - WINDOW_DAYS * 86400_000;

  // Compute the worst-case gap observed between consecutive
  // rotations AND the gap from the most-recent rotation to now —
  // shared classifier so the 3 call sites (here, avatar ring in
  // ConversationList, avatar ring in ContactList) stay in lock-step.
  const healthInfo = (() => {
    const label = classifyRotationHealth(entries);
    if (!label) return null;
    // Re-derive the worst-gap days for the tooltip only. Cheap; no
    // need to thread it back out of the classifier.
    let worstMs = Date.now() - entries[entries.length - 1].ts;
    for (let i = 1; i < entries.length; i++) {
      const gap = entries[i].ts - entries[i - 1].ts;
      if (gap > worstMs) worstMs = gap;
    }
    return {
      color: HEALTH_DOT_CLASS[label],
      label,
      days: Math.round(worstMs / 86400_000),
    };
  })();

  return (
    <div className={className} data-testid="rotation-sparkline">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        <span className="flex items-center gap-1.5">
          {healthInfo && (
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${healthInfo.color}`}
              title={`Longest gap: ${healthInfo.days}d (${healthInfo.label})`}
              data-testid="rotation-health-dot"
              data-health={healthInfo.label}
            />
          )}
          {label}
        </span>
        <span>{entries.length} rotations</span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        aria-label="Key rotation cadence over the last 42 days"
        className="block"
      >
        {/* Axis */}
        <line
          x1={PADDING_X} y1={HEIGHT / 2}
          x2={WIDTH - PADDING_X} y2={HEIGHT / 2}
          stroke="currentColor"
          strokeOpacity="0.15"
          strokeWidth="1"
        />
        {/* 7-day gridline ticks */}
        {[1, 2, 3, 4, 5].map((i) => {
          const x = PADDING_X + (i / 6) * (WIDTH - 2 * PADDING_X);
          return (
            <line
              key={i}
              x1={x} y1={HEIGHT / 2 - 3}
              x2={x} y2={HEIGHT / 2 + 3}
              stroke="currentColor"
              strokeOpacity="0.1"
              strokeWidth="1"
            />
          );
        })}
        {/* Rotation dots */}
        {entries.map((e, i) => {
          const x = _xForTs(e.ts);
          const faded = e.ts < cutoff;
          const isLast = i === entries.length - 1;
          return (
            <circle
              key={`${e.ts}-${i}`}
              cx={x}
              cy={HEIGHT / 2}
              r={isLast ? DOT_R + 1 : DOT_R}
              className={
                isLast
                  ? 'fill-emerald-400'
                  : faded
                  ? 'fill-slate-500'
                  : 'fill-sky-400'
              }
            >
              <title>{`${_fmt(e.ts)} · ${e.fp}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-0.5">
        <span>-42d</span>
        <span>now</span>
      </div>
    </div>
  );
};

export default RotationCadenceSparkline;
